import { FC } from "react";
import { Card, Button, Select, Checkbox, Switch } from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  SoundOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import useSummaryView from "../hooks/useSummaryView";
import { AssessmentAISummaryProps } from "../../../types/assessment";
import AssessmentAISummaryHandler from "./AssessmentAISummaryHandler";

const AssessmentAISummary: FC<AssessmentAISummaryProps> = ({
  selectedAssessment,
}) => {
  const {
    errors,
    isLoading,
    isEditMode,
    segmentData,
    toggledViews,
    playingSegment,
    filteredSegments,
    selectedSegments,
    togglePlay,
    splitTextIntoPoints,
    handleSegmentToggle,
    handleSegmentViewToggle,
  } = useSummaryView(selectedAssessment);

  return (
    <AssessmentAISummaryHandler
      errors={errors}
      data={segmentData}
      isLoading={isLoading}
    >
      <div className="assessment-summary-content">
        <div className="header-section">
          <div className="header-icon">
            <SoundOutlined />
          </div>
          <h1 className="header-title">
            Assessment Summary - {selectedAssessment?.key?.replace("-", " ")}
          </h1>
        </div>

        <Card className="controls-card">
          <div className="controls-content">
            <div className="control-group">
              <span className="control-label">Segments:</span>
              <Select
                className="segments-select"
                size="small"
                placeholder={`${selectedSegments.size}/${segmentData.length} selected`}
                dropdownRender={() => (
                  <div style={{ padding: "8px" }}>
                    {segmentData.map((segment) => (
                      <div
                        key={segment.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "4px 0",
                        }}
                      >
                        <Checkbox
                          checked={selectedSegments.has(segment.id)}
                          onChange={() => handleSegmentToggle(segment.id)}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#374151",
                              cursor: "pointer",
                            }}
                          >
                            {segment.title}
                          </span>
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        </Card>

        <div className="segments-container">
          {filteredSegments.map((segment) => (
            <Card key={segment.id} className="segment-card">
              <div className="segment-content">
                <div className="segment-header">
                  <div className="segment-title-section">
                    <Button
                      disabled
                      className="play-button"
                      icon={
                        playingSegment === segment.id ? (
                          <PauseCircleOutlined />
                        ) : (
                          <PlayCircleOutlined />
                        )
                      }
                      onClick={() => togglePlay(segment.id)}
                    />
                    <h3 className="segment-title">{segment.title}</h3>
                  </div>
                  {!isEditMode && (
                    <div className="view-toggle-section">
                      <span className="toggle-label">Summary</span>
                      <Switch
                        checked={toggledViews.has(segment.id)}
                        onChange={() => handleSegmentViewToggle(segment.id)}
                        size="small"
                      />
                      <span className="toggle-label">Details</span>
                    </div>
                  )}
                </div>

                <div className="segment-body">
                  <div className="view-mode">
                    {toggledViews.has(segment.id) ? (
                      <div className="detailed-view">
                        <div className="positive-aspects">
                          <h4 className="positive-title">Positive Aspects</h4>
                          <ol className="positive-list">
                            {segment.positiveAspects ? (
                              splitTextIntoPoints(segment.positiveAspects).map(
                                (point, index) => (
                                  <li key={index} className="positive-item">
                                    <span className="bullet">•</span>
                                    <span>{point}</span>
                                  </li>
                                )
                              )
                            ) : (
                              <li className="positive-item">
                                <span className="bullet">•</span>
                                <span>
                                  No specific positive points identified
                                </span>
                              </li>
                            )}
                          </ol>
                        </div>
                        <div className="negative-aspects">
                          <h4 className="negative-title">
                            Areas for Improvement
                          </h4>
                          <ul className="negative-list">
                            {segment.negativeAspects ? (
                              splitTextIntoPoints(segment.negativeAspects).map(
                                (point, index) => (
                                  <li key={index} className="negative-item">
                                    <span className="bullet">•</span>
                                    <span>{point}</span>
                                  </li>
                                )
                              )
                            ) : (
                              <li className="negative-item">
                                <span className="bullet">•</span>
                                <span>
                                  No specific improvement areas identified
                                </span>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <div className="summary-view">
                        <RobotOutlined className="summary-icon" />
                        <div className="summary-content">
                          <h4 className="summary-title">AI Summary</h4>
                          <p className="summary-text">{segment.summary}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AssessmentAISummaryHandler>
  );
};

export default AssessmentAISummary;
